import React from 'react';

import { isEmpty } from 'es-toolkit/compat';
import { createBrowserRouter, type RouteObject, RouterProvider } from 'react-router';

import { FullscreenLoader } from '@hrlogics/react-components';

import { RedirectPage } from 'src/components/common';
import { RequiredActionsGate } from 'src/components/router';
import { useRoutes } from 'src/hooks/router';
import { LayoutResolver } from 'src/layouts';
import { type RouteProps } from 'src/types/router';

import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ErrorBoundaryFallback } from './components/shared/ErrorBoundaryFallback';
import { FilterStorageProvider } from './providers/FilterStorageProvider';

export const AppRouter: React.FC = () => {
  const routes = useRoutes();

  const router = React.useMemo(() => {
    if (isEmpty(routes)) return null;

    const renderedRoutes = renderNestedRoutes(routes, true);
    const router = createBrowserRouter(renderedRoutes);

    return router;
  }, [routes]);

  if (!router) return <FullscreenLoader />;

  return <RouterProvider router={router} />;
};

const RootRouteWrapper: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
      <RequiredActionsGate>
        <FilterStorageProvider>
          <LayoutResolver>{children}</LayoutResolver>
        </FilterStorageProvider>
      </RequiredActionsGate>
    </ErrorBoundary>
  );
};

const renderNestedRoutes = (routesProps: RouteProps[] = [], isRoot?: boolean): RouteObject[] => {
  const renderRouteContent = (routeProps: RouteProps): Omit<RouteObject, 'index'> => {
    const { element, childRoutes, ...restProps } = routeProps;

    const componentToRender = element !== null ? element : <RedirectPage />;

    const elementProp = isRoot ? (
      <RootRouteWrapper>{componentToRender}</RootRouteWrapper>
    ) : (
      componentToRender
    );

    const childrenProp =
      childRoutes && childRoutes.length > 0 ? renderNestedRoutes(childRoutes) : undefined;

    return {
      ...restProps,
      element: elementProp,
      children: childrenProp,
    };
  };

  return routesProps.map(routeProps => renderRouteContent(routeProps));
};
